import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Gift, CheckCircle } from "lucide-react";

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState("");
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    referral_code: searchParams.get('ref') || ''
  });

  const from = location.state?.from?.pathname || '/';

  // Validate referral code
  useEffect(() => {
    const validateReferral = async () => {
      if (formData.referral_code && formData.referral_code.length >= 4) {
        try {
          const response = await axios.get(`${API}/referrals/validate/${formData.referral_code}`);
          setReferralValid(response.data.valid);
          setReferrerName(response.data.referrer_name || "");
        } catch {
          setReferralValid(false);
        }
      } else {
        setReferralValid(null);
      }
    };
    validateReferral();
  }, [formData.referral_code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      login(response.data.access_token, response.data.user);
      
      if (!isLogin && response.data.user.wallet_balance > 0) {
        toast.success(`🎉 रजिस्ट्रेशन सफल! आपको ₹${response.data.user.wallet_balance} वेलकम बोनस मिला!`);
      } else {
        toast.success(isLogin ? "स्वागत है!" : "रजिस्ट्रेशन सफल!");
      }
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "कुछ गलत हो गया");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-orange-500 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          वापस जाएं
        </button>

        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-rajdhani font-bold text-2xl">उ</span>
          </div>
          <div>
            <h1 className="font-rajdhani font-bold text-2xl text-gray-800">
              उद्यमी मित्र
            </h1>
            <p className="text-xs text-gray-8000">UDYAMI MITRA</p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-rajdhani font-bold text-xl text-gray-800 mb-6">
            {isLogin ? 'लॉगिन करें' : 'नया खाता बनाएं'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label className="text-gray-600">नाम</Label>
                  <Input
                    type="text"
                    placeholder="आपका नाम"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-industrial mt-1"
                    required
                    data-testid="name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-600">फ़ोन नंबर</Label>
                  <Input
                    type="tel"
                    placeholder="9876543210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-industrial mt-1"
                    required
                    data-testid="phone-input"
                  />
                </div>
                
                {/* Referral Code */}
                <div>
                  <Label className="text-gray-600 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-orange-500" />
                    रेफरल कोड (वैकल्पिक)
                  </Label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="ABC1234"
                      value={formData.referral_code}
                      onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                      className={`input-industrial mt-1 uppercase ${
                        referralValid === true ? 'border-emerald-500' : 
                        referralValid === false ? 'border-red-500' : ''
                      }`}
                      data-testid="referral-input"
                    />
                    {referralValid === true && (
                      <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                    )}
                  </div>
                  {referralValid === true && referrerName && (
                    <p className="text-xs text-emerald-500 mt-1">
                      🎉 {referrerName} के रेफरल से आपको ₹25 बोनस मिलेगा!
                    </p>
                  )}
                  {referralValid === false && (
                    <p className="text-xs text-red-500 mt-1">
                      अमान्य रेफरल कोड
                    </p>
                  )}
                </div>
              </>
            )}

            <div>
              <Label className="text-gray-600">ईमेल</Label>
              <Input
                type="email"
                placeholder="aapka@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-industrial mt-1"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label className="text-gray-600">पासवर्ड</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input-industrial mt-1"
                required
                minLength={6}
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary rounded-sm"
              data-testid="submit-btn"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isLogin ? 'लॉगिन' : 'रजिस्टर'}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-gray-8000">
              {isLogin ? 'खाता नहीं है?' : 'पहले से खाता है?'}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-orange-500 hover:underline ml-2"
                data-testid="toggle-auth-btn"
              >
                {isLogin ? 'रजिस्टर करें' : 'लॉगिन करें'}
              </button>
            </p>
          </div>
        </div>

        {/* Demo Credentials */}
        {isLogin && (
          <div className="mt-4 p-4 bg-white/50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-8000 mb-2">Demo Admin Login:</p>
            <p className="text-xs text-gray-600">Email: admin@udyamimitra.in</p>
            <p className="text-xs text-gray-600">Password: Admin@123</p>
          </div>
        )}
      </div>
    </div>
  );
}
